import axios from "axios";
import { subDays } from "date-fns";
import { Amplify } from "aws-amplify";
import { Hub, I18n } from "aws-amplify/utils";
import { useNavigate } from "react-router-dom";
import { Authenticator, Heading, translations } from "@aws-amplify/ui-react";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut, updateUserAttributes } from "aws-amplify/auth";
// @mui
import { Typography, useTheme } from "@mui/material";
//
import { formFields, WEB_API } from "src/config";

Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: 'us-east-1:0e40e5d8-ec01-439c-8cf5-006cf3d7ffc4',
      userPoolId: 'us-east-1_xw1uepfCG',
      userPoolClientId: '5itppitsqv3mirmpcp6ohaqh9u'
    },
  }
});

// AWS.config.update({
//   accessKeyId: COGNITO_API.accessKeyId,
//   secretAccessKey: COGNITO_API.secretAccessKey,
//   region: COGNITO_API.region,
// });

I18n.putVocabularies(translations);
I18n.setLanguage('pt');
I18n.putVocabularies({
  pt: {
    'Sign In': 'Login',
    'Create Account': 'Cadastre-se',
    'Password must have at least 8 characters': 'A senha deve ter no mínimo 8 caracteres',
    'An account with the email already exists.': 'Uma conta com esse email já existe',
    'Email': 'Email',
    'Confirm': 'Confirmar',
    'Reset Password': 'Redefinir senha'
  }
});

const listener = (data) => {
  switch (data.payload.event) {
    case 'signedIn':
      localStorage.removeItem('user');
      if (window.location.search?.includes('redirect')) {
        const search = new URLSearchParams(window.location.search);
        window.location.href = `/${search.get('redirect')}`;
      }
      else window.location.href = '/';
      break;
    case 'signedOut':
      localStorage.removeItem('user');
      localStorage.removeItem('cart');
      // window.location.href = '/';
      break;
    default:
      // window.location.href = '/';
      break;
  }
}

Hub.listen('auth', listener);

const initialState = {
  user: undefined,
  givenTime: 30,
  deliveryStatus: false,
  lastMessagesUpdate: null,
  conversationsReadStatus: null,
  cart: [],
  orders: [],
  setConversationsReadStatus: () => { },
  addToCart: () => { },
  updateCart: () => { },
  updateCartMultiple: () => { },
  removeCartItem: () => { },
  cleanCart: () => { },
  putOrder: () => { },
  setDeliveryStatus: () => { },
  setGivenTime: () => { },
  setLocalUser: () => { },
  logout: () => { },
  setLastMessagesPointer: () => { },
  getIdToken: () => Promise.resolve(),
  updateUserAttributes: () => Promise.resolve()
};

const AppContext = createContext(initialState);

function AppContextProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [user, setUser] = useState();
  // const [orders, setOrders] = useState([]);
  const [givenTime, setLocalGivenTime] = useState(30);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [conversationsStatus, setConversationsStatus] = useState(null);

  const lastMessagesUpdate = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const user = await getCurrentUser();
        let attrs = await fetchUserAttributes();
        attrs = {
          name: attrs.name,
          phone: attrs.phone_number,
          email: attrs.email,
          street: attrs['custom:street'],
          district: attrs['custom:district'],
          ref: attrs['custom:ref']
        }
        setUser(attrs);
        setCognitoUser(user);
        if (localStorage.getItem('cart'))
          setCart(JSON.parse(localStorage.getItem('cart')));
      } catch {
        setCognitoUser(null);
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
          if (user.name && user.phone && (user.createdAt + (4 * 60 * 60 * 1000) > Date.now())) { // local user during 4 hours
            setUser(user);

            // if (localStorage.getItem('orders')) {
            //   let orders = JSON.parse(localStorage.getItem('orders'));
            //   orders = orders.filter(e => e.createdAt + (24 * 60 * 60 * 1000) > Date.now());

            //   localStorage.setItem('orders', JSON.stringify(orders));
            //   setOrders(orders);
            // }
          }
          else {
            setUser(null);
            localStorage.removeItem('user');
            localStorage.removeItem('orders');
            localStorage.removeItem('cart');
          }
        } else {
          setUser(null);
        }

        if (localStorage.getItem('cart'))
          setCart(JSON.parse(localStorage.getItem('cart')));
      }

      if (localStorage.getItem('lastMessagesUpdate'))
        lastMessagesUpdate.current = Number(localStorage.getItem('lastMessagesUpdate'));
      else lastMessagesUpdate.current = subDays(Date.now(), 1).getTime();

      if (localStorage.getItem('conversationsReadStatus'))
        setConversationsStatus(JSON.parse(localStorage.getItem('conversationsReadStatus')));
    })()

    axios.request({
      url: `${WEB_API}/get_hourly`,
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*'
      }
    }).then(resp => {
      setDeliveryStatus(resp.data);
      setLocalGivenTime(Number(resp.data.givenTime));
    })

    // axios.request({
    //   url: `${WEB_API}/get_given_time`,
    //   method: 'GET',
    //   headers: {
    //     'Access-Control-Allow-Origin': '*',
    //     Accept: '*/*'
    //   }
    // }).then(resp => {
    //   setLocalGivenTime(Number(resp.data));
    //   localStorage.setItem('givenTime', Number(resp.data));
    // }).catch(e => console.log(e))
  }, []);

  const setLocalUser = useCallback((values) => {
    const user = { ...values, createdAt: Date.now() };
    if (!user.userId) {
      user.userId = crypto.randomUUID();
    }
    setUser(user);
    localStorage.setItem('user', JSON.stringify(user));
  }, [setUser]);

  const addToCart = useCallback((item) => {
    const newCart = [...cart, { ...item, availableQtd: item.q, q: 1 }];
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  }, [cart, setCart]);

  const removeCartItem = useCallback((id) => {
    const idx = cart.findIndex(e => e.localId === id);
    const newCart = [...cart];
    newCart.splice(idx, 1);

    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  }, [cart, setCart]);

  const updateCart = useCallback((id, updates) => {
    const idx = cart.findIndex(e => e.localId === id);
    const newCart = [...cart];
    newCart[idx] = {
      ...cart[idx],
      ...updates
    }

    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  }, [cart, setCart]);

  const updateCartMultiple = useCallback((items) => {
    let newCart = [...cart];
    items.forEach(i => {
      // const targetItems = newCart.filter(e => e.id === i.id);
      newCart = newCart.map(ti => {
        if (ti.id === i.id)
          return {
            ...ti,
            ...i
          }
        return ti;
      });
    })

    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  }, [cart, setCart]);

  const cleanCart = useCallback(() => {
    setCart([]);
    localStorage.setItem('cart', JSON.stringify([]));
  }, [setCart]);

  // const putOrder = useCallback((order) => {
  //   const newOrders = [...orders, order];
  //   setOrders(newOrders);

  //   localStorage.setItem('orders', JSON.stringify(newOrders));
  // }, [orders, setOrders]);

  const logout = useCallback(async () => {
    await signOut();
    setCognitoUser(null);
    setUser(null);
    // setOrders([]);
    setCart([]);
    localStorage.removeItem('orders');
    localStorage.removeItem('cart');
    localStorage.removeItem('user');

    navigate('/');
    // eslint-disable-next-line
  }, [setLocalUser]);

  const setLastMessagesPointer = useCallback((value) => {
    lastMessagesUpdate.current = value;
    localStorage.setItem('lastMessagesUpdate', value);
    localStorage.setItem('lastMessagesFlagUpdate', value);
  }, []);

  const setConversationsReadStatus = useCallback((value) => {
    setConversationsStatus(value);
    localStorage.setItem('conversationsReadStatus', JSON.stringify(value));
  }, []);

  const getIdToken = useCallback(async (force = false) => {
    try {
      // eslint-disable-next-line
      const session = await fetchAuthSession({ forceRefresh: force });
      return session?.tokens?.idToken?.toString();
    } catch (e) {
      logout();
    }
    return null;
  }, [logout]);

  const updateUserAttributesUser = useCallback(async (attrs) => {
    await updateUserAttributes({
      userAttributes: attrs
    })
    let newAttrs = await fetchUserAttributes();
    newAttrs = {
      name: newAttrs.name,
      phone: newAttrs.phone_number,
      email: newAttrs.email,
      street: newAttrs['custom:street'],
      district: newAttrs['custom:district']
    }
    setUser(newAttrs);
  },
    []
  );

  const contextValues = useMemo(() => ({
    user,
    deliveryStatus,
    givenTime,
    cart,
    // orders,
    lastMessagesUpdate,
    conversationsReadStatus: conversationsStatus,
    setConversationsReadStatus,
    setLastMessagesPointer,
    addToCart,
    updateCart,
    updateCartMultiple,
    cleanCart,
    removeCartItem,
    // putOrder,
    setLocalUser,
    logout,
    getIdToken,
    updateUserAttributes: updateUserAttributesUser
  }), [user, deliveryStatus, givenTime, cart, lastMessagesUpdate, conversationsStatus, updateCartMultiple, setConversationsReadStatus, setLastMessagesPointer, addToCart, updateCart, removeCartItem, cleanCart, setLocalUser, logout, getIdToken, updateUserAttributesUser]);

  const components = {
    ConfirmResetPassword: {
      Header() {
        const theme = useTheme();
        return (
          <Heading
            padding={`2 0 0 0`}
            level={3}
          >
            Redefinir senha
            <Typography variant='body1' sx={{ mt: 2 }} color={theme.palette.grey[700]}>
              Um código de confirmação foi enviado para o seu email.
              Insira-o no campo abaixo e defina uma nova senha.
            </Typography>
          </Heading>
        );
      },
    },
  }

  if (cognitoUser)
    return (
      <Authenticator formFields={formFields} components={components} loginMechanisms={['email']}>
        { // eslint-disable-next-line no-unused-vars
          ({ signOut, user }) => {
            return (
              // eslint-disable-next-line react/jsx-no-constructed-context-values
              <AppContext.Provider
                value={contextValues}
              >
                {children}
              </AppContext.Provider>
            )
          }
        }
      </Authenticator >
    );

  return (
    <AppContext.Provider
      value={contextValues}
    >
      {children}
    </AppContext.Provider>
  );
}

export { AppContextProvider, AppContext };